import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';

import { fetchFFLBadgeCount } from '../actions/AdminFFLAction';
import {
  getModuleBadgeCounts,
  markModuleViewed,
  type BadgeModule,
} from '../actions/AdminModuleBadgeAction';
import { getNotifications } from '../actions/AdminNotificationAction';

import type { RootState } from 'store';

export interface AdminGlobalState {
  badgeCounts: Record<string, number>;
  notifications: {
    total_records: number;
    // unread notifications of the user - what the bell badge shows
    unread_count?: number;
    data: any[];
  } | null;
  loadingNotifications: boolean;
  loadingMoreNotifications: boolean;
  isMobile: boolean;
}

const initialState: AdminGlobalState = {
  badgeCounts: {},
  notifications: null,
  loadingNotifications: false,
  loadingMoreNotifications: false,
  isMobile: false,
};

export const badgeCountsSelector = (state: RootState) => state.admin.adminGlobal.badgeCounts;
export const notificationsSelector = (state: RootState) => state.admin.adminGlobal.notifications;
export const isMobileSelector = (state: RootState) => state.admin.adminGlobal.isMobile;
export const loadingNotificationsSelector = (state: RootState) =>
  state.admin.adminGlobal.loadingNotifications;

// Counts the API couldn't give are left out, so a failed call keeps the badges as they are
const getModuleCounts = (response: any): Record<string, number> =>
  response?.status && response.data ? response.data : {};

export const fetchBadgeCounts = createAsyncThunk('adminGlobal/fetchBadgeCounts', async () => {
  const [ffl, moduleResponse] = await Promise.all([fetchFFLBadgeCount(), getModuleBadgeCounts()]);

  return {
    ffl,
    ...getModuleCounts(moduleResponse),
  };
});

// The user opened a list (orders, transfer requests, ...): its badge clears and the rest refresh
export const markModuleViewedAndRefresh = createAsyncThunk(
  'adminGlobal/markModuleViewed',
  async (module: BadgeModule) => {
    const [ffl, moduleResponse] = await Promise.all([
      fetchFFLBadgeCount(),
      markModuleViewed(module),
    ]);
    return { ffl, ...getModuleCounts(moduleResponse) };
  }
);

export const NOTIFICATION_PAGE_SIZE = 10;

// The header bell only lists unread notifications, so total_records is the unread count
const fetchUnreadNotifications = async (limit: number) => {
  const response: any = await getNotifications({ unread: true }, limit);
  if (response?.status) return response.data;
  return { total_records: 0, data: [] };
};

export const fetchNotifications = createAsyncThunk('adminGlobal/fetchNotifications', async () =>
  fetchUnreadNotifications(NOTIFICATION_PAGE_SIZE)
);

// "Load more" re-reads from the top with a bigger limit instead of asking for the next page:
// items marked read drop out of the unread list, so page offsets would skip notifications
export const fetchMoreNotifications = createAsyncThunk(
  'adminGlobal/fetchMoreNotifications',
  async (_, { getState }) => {
    const loaded = (getState() as RootState).admin.adminGlobal.notifications?.data.length ?? 0;
    return fetchUnreadNotifications(loaded + NOTIFICATION_PAGE_SIZE);
  }
);

// Opening the notification panel marks every notification read, so the badge drops to 0.
export const markAllNotificationsRead = createAsyncThunk(
  'adminGlobal/markAllNotificationsRead',
  async () => {
    const response: any = await markAllNotificationsRead();
    if (!response?.status) throw new Error(response?.message);
  }
);

const adminGlobalSlice = createSlice({
  name: 'adminGlobal',
  initialState,
  reducers: {
    setBadgeCounts(state, action: PayloadAction<Record<string, number>>) {
      state.badgeCounts = action.payload;
    },
    setNotifications(state, action: PayloadAction<AdminGlobalState['notifications']>) {
      state.notifications = action.payload;
    },
    setIsMobile(state, action: PayloadAction<boolean>) {
      state.isMobile = action.payload;
    },
    notificationMarkedRead(state, action: PayloadAction<string>) {
      if (!state.notifications) return;
      const remaining = state.notifications.data.filter((n) => n.id !== action.payload);
      if (remaining.length === state.notifications.data.length) return;
      state.notifications.data = remaining;
      state.notifications.total_records = Math.max(0, state.notifications.total_records - 1);
    },
    allNotificationsMarkedRead(state) {
      state.notifications = { total_records: 0, data: [] };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBadgeCounts.fulfilled, (state, action) => {
        state.badgeCounts = { ...state.badgeCounts, ...action.payload };
      })
      .addCase(markModuleViewedAndRefresh.pending, (state, action) => {
        // clear the opened list's badge straight away
        state.badgeCounts = { ...state.badgeCounts, [action.meta.arg]: 0 };
      })
      .addCase(markModuleViewedAndRefresh.fulfilled, (state, action) => {
        state.badgeCounts = { ...state.badgeCounts, ...action.payload };
      })

      .addCase(fetchNotifications.pending, (state) => {
        state.loadingNotifications = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.notifications = action.payload;
        state.loadingNotifications = false;
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.loadingNotifications = false;
      })

      .addCase(fetchMoreNotifications.pending, (state) => {
        state.loadingMoreNotifications = true;
      })
      .addCase(fetchMoreNotifications.fulfilled, (state, action) => {
        state.notifications = action.payload;
        state.loadingMoreNotifications = false;
      })
      .addCase(fetchMoreNotifications.rejected, (state) => {
        state.loadingMoreNotifications = false;
      });
  },
});

export const {
  setBadgeCounts,
  setNotifications,
  setIsMobile,
  notificationMarkedRead,
  allNotificationsMarkedRead,
} = adminGlobalSlice.actions;
export default adminGlobalSlice.reducer;
